import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Search, ShoppingBag, Menu } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "./ui/sheet";
import { useStore } from "../context/StoreContext";
import { http } from "../lib/api";

export default function Header() {
  const { cartCount, setCartOpen, setSearchOpen, settings } = useStore();
  const [nav, setNav] = useState([]);
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const loc = useLocation();
  const isHome = loc.pathname === "/";

  useEffect(() => {
    http.get("/navigation").then((r) => setNav((r.data || []).filter((i) => i.visible !== false))).catch(() => {});
  }, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const solid = scrolled || !isHome;

  return (
    <header
      className={`sticky top-0 z-40 transition-colors duration-300 border-b ${
        solid ? "bg-black/90 backdrop-blur-md border-white/10" : "bg-transparent border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="px-4 lg:px-8 h-16 flex items-center justify-between gap-4">
        {/* left: mobile menu + desktop nav */}
        <div className="flex items-center gap-6 flex-1">
          <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
            <SheetTrigger asChild>
              <button className="lg:hidden text-zinc-100" data-testid="mobile-menu-trigger" aria-label="Menu">
                <Menu size={22} />
              </button>
            </SheetTrigger>
            <SheetContent side="left" className="bg-[#070708] border-zinc-800 w-[80%] sm:w-[340px] p-0">
              <div className="p-6">
                <span className="font-display text-3xl tracking-widest text-white">STREETSINS</span>
              </div>
              <nav className="flex flex-col">
                {nav.map((it) => (
                  <Link key={it.label} to={it.link} onClick={() => setMenuOpen(false)}
                    className="px-6 py-4 border-t border-zinc-900 text-sm uppercase tracking-widest text-zinc-200 hover:text-accent"
                    data-testid={`mobile-nav-${it.label.toLowerCase()}`}>
                    {it.label}
                  </Link>
                ))}
                <Link to="/track" onClick={() => setMenuOpen(false)}
                  className="px-6 py-4 border-t border-zinc-900 text-sm uppercase tracking-widest text-zinc-400">
                  Track Order
                </Link>
              </nav>
            </SheetContent>
          </Sheet>
          <nav className="hidden lg:flex items-center gap-7">
            {nav.map((it) => (
              <Link key={it.label} to={it.link}
                className="text-xs uppercase tracking-widest text-zinc-300 hover:text-white transition-colors"
                data-testid={`nav-${it.label.toLowerCase()}`}>
                {it.label}
              </Link>
            ))}
          </nav>
        </div>

        {/* center: logo */}
        <Link to="/" className="font-display text-3xl sm:text-4xl tracking-widest text-white leading-none"
          data-testid="nav-brand-logo">
          {settings?.store_name?.toUpperCase() || "STREETSINS"}
        </Link>

        {/* right */}
        <div className="flex items-center gap-4 flex-1 justify-end">
          <button onClick={() => setSearchOpen(true)} className="text-zinc-200 hover:text-white"
            aria-label="Search" data-testid="nav-search-trigger">
            <Search size={20} />
          </button>
          <button onClick={() => setCartOpen(true)} className="relative text-zinc-200 hover:text-white"
            aria-label="Cart" data-testid="nav-cart-trigger">
            <ShoppingBag size={20} />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 text-[10px] font-bold text-white w-4 h-4 flex items-center justify-center"
                style={{ background: "var(--ss-accent)" }} data-testid="cart-count-badge">
                {cartCount}
              </span>
            )}
          </button>
        </div>
      </div>
    </header>
  );
}
